export interface WorkflowActionField {
  type: string;
  name?: string;
  description?: string;
  [key: string]: unknown;
}

// A workflow action as stored by the API (see getWorkflowRequestAction for the reverse mapping).
export interface WorkflowAction {
  type?: string;
  fields?: WorkflowActionField[];
  message?: string;
  message_type?: string;
  message_before?: string;
  message_after?: string;
  receiver_email_addresses?: string[];
  sender_name?: string;
  subject?: string;
  sender_email_address?: string;
}

// A workflow as stored by the API in pf_default_workflow and pf_custom_workflows.
// The default workflow has a single action and no name; custom workflows have an action list.
export interface Workflow {
  type: string;
  enabled: boolean;
  name?: string;
  description?: string;
  trigger_type?: string;
  trigger_value?: string;
  action?: WorkflowAction | WorkflowAction[];
}

export interface WorkflowFormAction {
  action_type: string;
  action_fields_custom: boolean;
  action_fields_name: boolean;
  action_fields_email: boolean;
  action_fields_phone_number: boolean;
  action_message_type: string;
  message_before: string;
  receiver_email_addresses: string;
  sender_name?: string;
  subject?: string;
  sender_email_address?: string;
  message_after: string;
  action_message: string;
  action_escalated_list: string;
  action_data_collection: string;
  message_close: string;
  // Fields added in the form have no type yet; getWorkflowRequestAction sets it.
  action_fields_custom_array?: Partial<WorkflowActionField>[];
}

// The values of a workflow form: the add/edit workflow popups and the escalation steps.
export interface WorkflowFormValues {
  name: string;
  description: string;
  trigger_type: string;
  trigger_value: string;
  // A newly added action only has the fields its type needs.
  action: Partial<WorkflowFormAction>[];
}

// Path to one action in WorkflowFormValues, e.g. `action.0`.
export type WorkflowActionPath = `action.${number}`;

// These stored action types are all edited under the single "escalate" option in the form.
const ESCALATION_ACTION_TYPES = new Set([
  'collect_and_escalate',
  'forward_to_email',
  'handle_with_crm',
  'close',
]);

/** Maps a stored workflow action to the workflow form's field values. */
export const getWorkflowFormAction = (action: WorkflowAction): WorkflowFormAction => {
  const hasField = (type: string) => Boolean(action.fields?.some((field) => field.type === type));
  const customFields = action.fields?.filter((field) => field.type === 'custom') ?? [];

  return {
    action_type:
      action.type && ESCALATION_ACTION_TYPES.has(action.type)
        ? 'escalate'
        : action.type || 'clarify',
    action_fields_custom: hasField('custom'),
    action_fields_name: hasField('name'),
    action_fields_email: hasField('email'),
    action_fields_phone_number: hasField('phone_number'),
    action_message_type: action.message_type || 'transcript',
    message_before: action.message_before || '',
    receiver_email_addresses: action.receiver_email_addresses
      ? action.receiver_email_addresses.join(', ')
      : '',
    sender_name: action.sender_name,
    subject: action.subject,
    sender_email_address: action.sender_email_address,
    message_after: action.message_after || '',
    action_message: action.message || '',
    action_escalated_list: action.type || '',
    action_data_collection: '',
    message_close: action.message || '',
    ...(customFields.length > 0 && { action_fields_custom_array: customFields }),
  };
};
